import type {
  DomainEntity,
  DomainEntityConfiguration,
  ConfigEntity,
  ConfigEntityConfiguration,
  ConfigEntityRootData,
  EntityConfigEndpoint,
  EntityRootData,
  GenerateDomainEntityId,
  PrefetchConfigEntityConfigEndpoint,
  UserInfo,
} from '@web-config-app/core';
import { isConfigEntity, pipeFunctions } from '@web-config-app/core';
import { sanitizeAndSyncDataForRequest } from '../sanitize-and-sync-data-for-request/sanitize-and-sync-data-for-request';
import { removeFieldsMatchingOmitIf } from '../remove-fields-matching-omit-if/remove-fields-matching-omit-if';
import { removeNullAndEmptyObjects } from '../remove-null-and-empty-objects/remove-null-and-empty-objects';
import { injectOperationEntityMetadataRequiredFields } from '../inject-operation-entity-metadata-required-fields/inject-operation-entity-metadata-required-fields';
import { addMetaFieldToBodyData } from '../add-meta-field-to-body-data/add-meta-field-to-body-data';
import { injectDomainEntityId } from '../inject-domain-entity-id/inject-domain-entity-id';
import { syncRelationshipsFromSchema } from '../sync-relationships-from-schema/sync-relationships-from-schema';

export type EntityTransformer = (data: EntityRootData) => EntityRootData;

export interface PrepareEntityBodyDataOptions {
  data: EntityRootData | ConfigEntityRootData | undefined;
  entity:
    | ConfigEntity
    | ConfigEntityConfiguration
    | DomainEntity
    | DomainEntityConfiguration;
  operationEndpoint: EntityConfigEndpoint | PrefetchConfigEntityConfigEndpoint;
  userInfo?: UserInfo;
  transformers?: EntityTransformer[];
  shouldInjectDomainEntityId?: boolean;
  generateDomainEntityId?: GenerateDomainEntityId;
}

/**
 * Prepares entity data for API requests by applying a series of transformations:
 * 1. Sanitizes and syncs data (removes empty objects, syncs localized arrays)
 * 2. Removes fields that match their omitIf conditions
 * 3. Syncs relationships to match actual data from schema annotations
 * 4. Applies custom transformers (if provided)
 * 5. Injects operation-specific entity metadata required fields
 * 6. Adds author meta field (for config entities, if userInfo provided)
 * 7. Injects domain entity ID (if shouldInjectDomainEntityId is true)
 *
 * @param options - Configuration object for data preparation
 * @returns Prepared body data ready for API request
 */
export const prepareEntityBodyData = ({
  data,
  entity,
  operationEndpoint,
  userInfo,
  transformers,
  shouldInjectDomainEntityId,
  generateDomainEntityId,
}: PrepareEntityBodyDataOptions):
  | EntityRootData
  | ConfigEntityRootData
  | undefined => {
  // Step 1: Sanitize and sync data
  /**
   * Removes empty object and syncs localized array lengths from the entityRootData and applies any custom transformers
   * before data is sent to the backend.
   */
  let processedData = sanitizeAndSyncDataForRequest(data);

  // Step 2: Remove fields that match their omitIf conditions
  processedData = removeFieldsMatchingOmitIf(processedData, entity);

  // omitIf removal can cause parent objects to become empty. Re-run empty-object/null pruning
  // to avoid sending `{}` where the field should be omitted.
  processedData = processedData
    ? (removeNullAndEmptyObjects(processedData as any) as any)
    : processedData;

  // Step 3: Sync relationships to match actual entity reference data.
  // This ensures stale relationship entries (e.g. from removed activity sets)
  // are cleaned up, and the relationships array exactly reflects what's in the data.
  // For ConfigEntity, entity.schema (form schema) lacks relationships, so pass
  // relationship definitions from entity.domainEntity.schema separately to
  // syncRelationshipsFromSchema so the sync can match annotations to
  // relationship entries.
  if (isConfigEntity(entity)) {
    const domainRelationships =
      entity.domainEntity?.schema?.properties?.relationships;
    processedData = syncRelationshipsFromSchema(
      processedData,
      entity.schema,
      domainRelationships,
    );
  } else if ('schema' in entity) {
    processedData = syncRelationshipsFromSchema(processedData, entity.schema);
  }

  // Step 4: Apply custom transformers if provided
  if (transformers?.length) {
    // Create a pipeline of transformations using pipe
    const applyTransformers = pipeFunctions(...transformers);
    processedData = applyTransformers(processedData);
  }

  // Step 5: Inject operation-specific metadata required fields
  let bodyData = injectOperationEntityMetadataRequiredFields(
    processedData,
    operationEndpoint,
  );

  // Step 6: Add author meta field for config entities (if userInfo available)
  if (isConfigEntity(entity) && userInfo) {
    bodyData = addMetaFieldToBodyData(bodyData, userInfo);
  }

  // Step 7: Inject domain entity ID (if configured)
  if (shouldInjectDomainEntityId && generateDomainEntityId) {
    bodyData = injectDomainEntityId(generateDomainEntityId, bodyData);
  }

  return bodyData;
};
